/**
 * Danh sách món cho ô chọn món trọng điểm (CEO góp ý 30/10/2026: "hiển thị danh
 * sách các món ra cho chọn, hoặc cho tìm tên và gợi ý gần giống rồi chọn").
 *
 * Máy lương so món trọng điểm bằng MÃ (salesteampay): GNPF so `orders.product`
 * (mã gói) hoặc `order_surcharges.catalog_id` (mã khoản phát sinh); GNPM so mã
 * món trong tin thu tiền GNPM gửi sang. Gõ tay sai một chữ là món không bao giờ
 * được tính mà không ai thấy — nên chọn từ danh sách, máy tự điền mã + tên.
 *
 * GNPM: bảng món GNPM gửi sang qua Center (CEO 1a 04/11/2026) — có cả món chưa ai
 * mua — cộng món đã từng có tin trả đủ; khi bảng chưa tới thì chỉ còn phần sau, món
 * thiếu vẫn gõ tay được.
 */
import type { CatalogProduct } from "@/features/products/catalogApi";
import type { SurchargeCatalogItem } from "@/features/products/constants";
import type { SearchableItem } from "@/features/products/packageSearch";

export type KeySystem = "gnpf" | "gnpm";

export type KeyItemOption = SearchableItem & {
  system: KeySystem;
  /** Nhóm hiển thị: "Gói", "Phát sinh", "Món GNPM". */
  group: string;
  /** Dòng phụ dưới tên (dòng sản phẩm / thương hiệu). */
  hint: string;
  /** Ảnh đại diện cỡ nhỏ; chỉ gói GNPF có (khoản phát sinh, món GNPM không có ảnh). */
  imageUrl?: string | undefined;
};

/** Khoá món = đúng `itemRef` máy chủ lưu: "<hệ>:<mã>". */
export function itemRefOf(system: KeySystem, code: string): string {
  return `${system}:${code.trim()}`;
}

/**
 * Món GNPF: gói ĐANG bán trước (theo thứ tự bảng giá), rồi khoản phát sinh đang bán.
 * `imageOf` — ảnh gói theo mã, cùng luật form đơn (bookingServiceImage.useServiceImageOf).
 */
export function gnpfOptions(
  products: readonly CatalogProduct[],
  surcharges: readonly SurchargeCatalogItem[],
  imageOf: (code: string) => string | undefined = () => undefined,
): KeyItemOption[] {
  const out: KeyItemOption[] = [];
  const seen = new Set<string>();
  for (const p of products) {
    if (!p.isActive || !p.code.trim()) continue;
    const key = itemRefOf("gnpf", p.code);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      code: p.code.trim(),
      label: p.name,
      system: "gnpf",
      group: "Gói",
      hint: p.lineName || p.brandName,
      imageUrl: imageOf(p.code),
    });
  }
  for (const s of surcharges) {
    if (s.isActive === false || !s.id.trim()) continue;
    const key = itemRefOf("gnpf", s.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, code: s.id.trim(), label: s.label, system: "gnpf", group: "Phát sinh", hint: "" });
  }
  return out;
}

export function gnpmOptions(items: readonly { code: string; name: string }[]): KeyItemOption[] {
  return items
    .filter((m) => m.code.trim() !== "")
    .map((m) => ({
      key: itemRefOf("gnpm", m.code),
      code: m.code.trim(),
      label: m.name || m.code,
      system: "gnpm" as const,
      group: "Món GNPM",
      hint: "",
    }));
}
